import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { TableSearchPreferencesConfig } from "../ConfigRecords";
import { ConfigService } from "../ConfigService";

/**
 * Valid config keys for search table preferences
 */
export type SearchTablePreferenceKey =
  | "collection_search_table_preferences"
  | "dataset_search_table_preferences"
  | "device_search_table_preferences"
  | "file_search_table_preferences"
  | "topic_search_table_preferences"
  | "message_path_search_table_preferences"
  | "event_search_table_preferences"
  | "session_search_table_preferences";

/**
 * Generate query key for a specific search table preference
 */
export function getQueryKey(configKey: SearchTablePreferenceKey) {
  return ["searchTablePreferences", configKey] as const;
}

/**
 * The shape returned for an org with no saved preferences.
 *
 * Exposed so route loaders can prime the cache with this sentinel after a
 * caught error, allowing the component-level fallback chain to render the
 * page with default columns/rows-per-page without a second fetch attempt.
 */
export const EMPTY_TABLE_SEARCH_PREFERENCES: TableSearchPreferencesConfig = {
  version: 1,
  org_scoped_preferences: {},
};

/**
 * Query factory for search table preferences. Returned object is spreadable
 * into `useQuery`, `queryClient.ensureQueryData`, etc.
 */
export function searchTablePreferencesQuery(
  configService: ConfigService,
  configKey: SearchTablePreferenceKey,
) {
  return {
    queryKey: getQueryKey(configKey),
    queryFn: async ({
      signal,
    }: {
      signal?: AbortSignal;
    }): Promise<TableSearchPreferencesConfig> => {
      const value =
        await configService.getUserConfig<TableSearchPreferencesConfig>(
          configKey,
          { abortSignal: signal },
        );

      return value ?? EMPTY_TABLE_SEARCH_PREFERENCES;
    },
    staleTime: Infinity,
  };
}

/**
 * Generic hook to fetch search table preferences for a specific config key.
 * Returns table preferences including columns and rows per page settings.
 *
 * @param configKey - The config key identifying which table preferences to fetch
 * @returns React Query result with table preferences data
 */
export function useSearchTablePreferences(configKey: SearchTablePreferenceKey) {
  const { configService } = useDomainServices();

  return useQuery(searchTablePreferencesQuery(configService, configKey));
}
